import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { type CollectionItem } from "@/lib/collection";
import { collectionWithStudio } from "@/lib/studio";
import { fetchPublishedStudioPieces } from "@/lib/studio-public.functions";
import { CARE_TIPS, ORDER_STEPS } from "@/lib/content";
import { absUrl } from "@/lib/site";
import { jsonLdScript, pageHead, PAGE_SEO, webPageGraph } from "@/lib/seo";
import { avifSrcSet } from "@/lib/photos";

import { Reveal } from "@/components/site/reveal";
import { Eyebrow } from "@/components/site/eyebrow";
import { CollectionCard } from "@/components/site/collection-card";

const CARD_SIZES = "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";

export const Route = createFileRoute("/collection")({
  loader: async () => {
    const studio = await fetchPublishedStudioPieces();
    return { items: collectionWithStudio(studio) };
  },
  component: CollectionPage,
  head: ({ loaderData }) => {
    const items = loaderData?.items ?? collectionWithStudio([]);
    const graph = webPageGraph(PAGE_SEO.collection);
    const first = items[0];
    const headOptions = {
      ...PAGE_SEO.collection,
      scripts: [
        jsonLdScript({
          ...graph,
          "@graph": [
            ...graph["@graph"],
            {
              "@type": "ItemList",
              "@id": `${absUrl("/collection")}#collection`,
              name: "Handmade doll clothes collection",
              numberOfItems: items.length,
              itemListElement: items.map((item, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: item.name,
                url: absUrl(`/collection#${item.slug}`),
                image: absUrl(item.photo.src),
                description: item.detail,
              })),
            },
          ],
        }),
      ],
    };
    return first
      ? pageHead({
          ...headOptions,
          preload: preloadFor(first),
        })
      : pageHead(headOptions);
  },
});

function preloadFor(item: CollectionItem) {
  if (item.photo.avif && item.photo.variants) {
    return {
      href: item.photo.avif,
      type: "image/avif" as const,
      imageSrcSet: avifSrcSet({
        src: item.photo.src,
        avif: item.photo.avif,
        width: item.photo.width,
        height: item.photo.height,
        variants: item.photo.variants,
      }),
      imageSizes: CARD_SIZES,
    };
  }
  const src = item.photo.src;
  const type = src.includes(".png")
    ? "image/png"
    : src.includes(".webp")
      ? "image/webp"
      : "image/jpeg";
  return { href: src, type };
}

function CollectionPage() {
  const { items } = Route.useLoaderData();
  return (
    <main id="main" tabIndex={-1}>
      <section className="relative overflow-hidden py-16 sm:py-24 lg:py-32">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-[-12rem] top-24 size-[30rem] rounded-full bg-gold-soft/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>THE COLLECTION</Eyebrow>
            <h1 className="mt-5 text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">
              New styles,{" "}
              <em className="accent-underline font-display italic text-primary">classic</em> charm.
            </h1>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              From everyday play outfits to special-occasion dresses, every piece here is knit,
              crocheted, or sewn by Ella herself in Midland, Michigan, and sized for 18-inch
              American-style dolls. Each listing shows what comes with the set, how it was made, and
              the kind of occasion it suits.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Ella is always adding new looks. If you don't see quite the right thing, ask about a{" "}
              <Link to="/personalization" className="text-gold-ink underline underline-offset-4">
                personalized made-to-order piece
              </Link>{" "}
              instead — colors, themes, and matching accessories can all be changed.
            </p>
          </Reveal>

          <h2 className="sr-only">Handmade pieces</h2>
          <ul className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item, i) => (
              <CollectionCard
                key={item.slug}
                item={item}
                description={item.detail}
                delay={(i % 3) * 90}
                sizes={CARD_SIZES}
                eager={i === 0}
                detailed
              />
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-secondary py-16 sm:py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[0.9fr_1fr] lg:px-10">
          <Reveal>
            <Eyebrow>SIZING &amp; FIT</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Made to fit{" "}
              <em className="accent-underline font-display italic text-primary">18-inch</em> dolls.
            </h2>
          </Reveal>
          <Reveal delay={120} className="space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              Every outfit on this page is made to fit 18-inch American-style dolls — the size most
              families already have at home. Ella works to that shape as she knits, crochets, and
              sews, so dresses slip on easily and sets sit the way they look in the photos.
            </p>
            <p>
              Doll Clothes by Ella Wise is an independent handmade shop and is not affiliated with,
              endorsed by, or connected to any doll brand or manufacturer. Brand names are only used
              to describe the doll size the clothes are made for.
            </p>
            <p>
              If your doll is a different size, or you're not sure what size you have, mention it
              when you{" "}
              <Link to="/contact" className="text-gold-ink underline underline-offset-4">
                get in touch
              </Link>{" "}
              and Ella will tell you honestly what she can do.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>CARING FOR HANDMADE PIECES</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              Gentle care,{" "}
              <em className="accent-underline font-display italic text-primary">long life.</em>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Handmade knit and crochet lasts beautifully when it's treated kindly. This is general
              guidance — if you're ever unsure about a particular piece, just ask Ella.
            </p>
          </Reveal>

          <ul className="mt-14 grid gap-5 sm:grid-cols-2">
            {CARE_TIPS.map((tip, i) => (
              <Reveal as="li" key={tip.title} delay={(i % 2) * 80}>
                <div className="h-full rounded-3xl border border-border/70 bg-card p-6 shadow-[var(--shadow-soft)]">
                  <h3 className="font-display text-lg">{tip.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tip.copy}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-plum py-16 text-plum-foreground sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow>HOW ORDERING WORKS</Eyebrow>
            <h2 className="mt-5 text-4xl leading-[1.08] sm:text-5xl">
              No cart. Just a <em className="font-display italic text-gold">conversation.</em>
            </h2>
            <p className="mt-6 max-w-xl leading-relaxed text-plum-foreground/75">
              Ella works with you one-on-one, the way she always has. Pricing and timing depend on
              the piece, and she'll confirm both with you personally before anything is made.
            </p>
          </Reveal>

          <ol className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
            {ORDER_STEPS.map((s, i) => (
              <Reveal as="li" key={s.title} delay={i * 90}>
                <div className="h-full rounded-3xl bg-plum-deep/40 p-7">
                  <span className="font-display text-4xl text-gold/60">0{i + 1}</span>
                  <div className="mt-5 stitch-rule text-gold" aria-hidden="true" />
                  <h3 className="mt-5 font-display text-xl">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-plum-foreground/75">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <div className="flex flex-col items-center gap-6 rounded-3xl bg-accent px-8 py-10 text-center md:flex-row md:justify-between md:text-left">
              <p className="max-w-2xl font-display text-xl leading-snug text-accent-foreground sm:text-2xl">
                See something you love? Get in touch and Ella will help you find the perfect piece —
                or make one just for you.
              </p>
              <Link
                to="/contact"
                className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Get in Touch <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
